import { ChangeEvent } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import dayjs, { Dayjs } from 'dayjs'
import { useIsTimesheetApproved } from '../../../../../../utils/hooks'
import { TimesheetStoreDispatch, TimesheetStoreState } from '../../../../../../store/timesheetStore'
import { acceptApprovalServices } from '../../../../../../store/slices/timesheet/timesheetApprovalDetails/timesheetApprovalDetailsSlice'
import { handleTimesheetServicesDetails, Services } from '../../../../../../store/slices/timesheet/timesheetPageSlice'
import { hasChanges } from '../../../../../../store/slices/timesheet/timesheetApprovalDetails/timesheetApprovalHasChangesSlice'

interface ServiceState {
	setStartTime: (time: string) => void
	setStartDate: (date: string) => void
	setEndTime: (time: string) => void
	setEndDate: (date: string) => void
	setPassengers: (count: number) => void
	startTime: string
	startDate: string
	endTime: string
	endDate: string
}

export const useServiceHandlers = (instance: number, state: ServiceState) => {
	const dispatch = useDispatch<TimesheetStoreDispatch>()
	const timesheetApprovalDetailsAccept = useSelector(
		(state: TimesheetStoreState) => state.timesheetApprovalDetailsAccept,
	)
	const timesheetPage = useSelector((state: TimesheetStoreState) => state.timesheetPage)
	const isTimeSheetApproved = useIsTimesheetApproved()

	const handleStartHoursProcessed = (newValue: Dayjs | null) => {
		if (
			newValue === null ||
			!dayjs(newValue).isValid() ||
			dayjs(`${state.startDate}T${newValue}`).isAfter(dayjs(`${state.endDate}T${state.endTime}`))
		)
			return

		const newTime = newValue.format('HH:mm')
		state.setStartTime(newTime)
		const newDateTime = dayjs(
			`${dayjs(timesheetPage.services[instance].service?.startDateTime).format('YYYY-MM-DD')}T${newTime}`,
		).format('YYYY-MM-DDTHH:mm:ss')

		if (isTimeSheetApproved) {
			const updatedServices = timesheetApprovalDetailsAccept.services.map((s, i) =>
				i === instance ? { ...s, startDateTime: newDateTime } : s,
			)
			dispatch(acceptApprovalServices(updatedServices))
		} else {
			const updatedServices = timesheetPage.services.map((s, i) =>
				i === instance ? { ...s, serviceExecution: { ...s.serviceExecution, startDateTime: newDateTime } } : s,
			)
			dispatch(handleTimesheetServicesDetails(updatedServices as Services[]))
		}
		dispatch(hasChanges({ hasChangesFromReviewer: true }))
	}

	const handleStartDateProcessed = (newValue: string) => {
		if (newValue === '' || dayjs(newValue).isAfter(dayjs(state.endDate))) return

		state.setStartDate(newValue)
		const newDateTime = dayjs(`${dayjs(newValue).format('YYYY-MM-DD')}T${state.startTime}`).format(
			'YYYY-MM-DDTHH:mm:ss',
		)

		if (isTimeSheetApproved) {
			const updatedServices = timesheetApprovalDetailsAccept.services.map((s, i) =>
				i === instance ? { ...s, startDateTime: newDateTime } : s,
			)
			dispatch(acceptApprovalServices(updatedServices))
		} else {
			const updatedServices = timesheetPage.services.map((s, i) =>
				i === instance ? { ...s, serviceExecution: { ...s.serviceExecution, startDateTime: newDateTime } } : s,
			)
			dispatch(handleTimesheetServicesDetails(updatedServices as Services[]))
		}
		dispatch(hasChanges({ hasChangesFromReviewer: true }))
	}

	const handleEndHoursProcessed = (newValue: Dayjs | null) => {
		if (
			newValue === null ||
			!dayjs(newValue).isValid() ||
			dayjs(`${state.endDate}T${newValue}`).isBefore(dayjs(`${state.startDate}T${state.startTime}`))
		)
			return

		const newTime = newValue.format('HH:mm')
		state.setEndTime(newTime)
		const newDateTime = dayjs(
			`${dayjs(timesheetPage.services[instance].service?.endDateTime).format('YYYY-MM-DD')}T${newTime}`,
		).format('YYYY-MM-DDTHH:mm:ss')

		if (isTimeSheetApproved) {
			const updatedServices = timesheetApprovalDetailsAccept.services.map((s, i) =>
				i === instance ? { ...s, endDateTime: newDateTime } : s,
			)
			dispatch(acceptApprovalServices(updatedServices))
		} else {
			const updatedServices = timesheetPage.services.map((s, i) =>
				i === instance ? { ...s, serviceExecution: { ...s.serviceExecution, endDateTime: newDateTime } } : s,
			)
			dispatch(handleTimesheetServicesDetails(updatedServices as Services[]))
		}
		dispatch(hasChanges({ hasChangesFromReviewer: true }))
	}

	const handleEndDateProcessed = (newValue: string) => {
		if (newValue === '' || dayjs(newValue).isBefore(dayjs(state.startDate))) return

		state.setEndDate(newValue)
		const newDateTime = dayjs(`${dayjs(newValue).format('YYYY-MM-DD')}T${state.endTime}`).format('YYYY-MM-DDTHH:mm:ss')

		if (isTimeSheetApproved) {
			const updatedServices = timesheetApprovalDetailsAccept.services.map((s, i) =>
				i === instance ? { ...s, endDateTime: newDateTime } : s,
			)
			dispatch(acceptApprovalServices(updatedServices))
		} else {
			const updatedServices = timesheetPage.services.map((s, i) =>
				i === instance ? { ...s, serviceExecution: { ...s.serviceExecution, endDateTime: newDateTime } } : s,
			)
			dispatch(handleTimesheetServicesDetails(updatedServices as Services[]))
		}
		dispatch(hasChanges({ hasChangesFromReviewer: true }))
	}

	const handlePassengersCountChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const value = Number(e.target.value)
		if (isNaN(value) || value < 0) return

		state.setPassengers(value)
		if (isTimeSheetApproved) {
			const updatedServices = timesheetApprovalDetailsAccept.services.map((s, i) =>
				i === instance ? { ...s, pax: value } : s,
			)
			dispatch(acceptApprovalServices(updatedServices))
		} else {
			const updatedServices = timesheetPage.services.map((s, i) =>
				i === instance ? { ...s, serviceExecution: { ...s.serviceExecution, pax: value } } : s,
			)
			dispatch(handleTimesheetServicesDetails(updatedServices as Services[]))
		}
		dispatch(hasChanges({ hasChangesFromReviewer: true }))
	}

	const handleCleanChange = (checked: boolean) => {
		if (isTimeSheetApproved) {
			const updatedServices = timesheetApprovalDetailsAccept.services.map((s, i) =>
				i === instance ? { ...s, isClean: checked } : s,
			)
			dispatch(acceptApprovalServices(updatedServices))
		} else {
			const updatedServices = timesheetPage.services.map((s, i) =>
				i === instance ? { ...s, serviceExecution: { ...s.serviceExecution, isClean: checked } } : s,
			)
			dispatch(handleTimesheetServicesDetails(updatedServices as Services[]))
		}
		dispatch(hasChanges({ hasChangesFromReviewer: true }))
	}

	return {
		handleStartHoursProcessed,
		handleStartDateProcessed,
		handleEndHoursProcessed,
		handleEndDateProcessed,
		handlePassengersCountChange,
		handleCleanChange,
	}
}
